import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@telegram-apps/telegram-ui";
import { openInvoice } from "@telegram-apps/sdk";
import { BookInfo } from "../../handlers/types";
import ApiService from "../../handlers/apiService";
import { t } from "../../utils/i18n/index";
import { useI18n } from "../../utils/i18n/I18nProvider";
import "./MagicBooks.css";
import { StorageService } from "../../services/LocalStorageService";

interface BookCardProps {
  book: BookInfo;
  onPurchaseBook: (bookId: string) => void;
  isPurchasing?: boolean;
  userBalance: number; // Add userBalance prop
  isFirstNewBook?: boolean;
}

// Memoize BookCard to prevent unnecessary rerenders
const BookCard = React.memo(function BookCard({
  book,
  onPurchaseBook,
  isPurchasing,
  userBalance,
  isFirstNewBook = false,
}: BookCardProps) {
  const { t } = useI18n();
  const [isHovered, setIsHovered] = useState(false);

  // Determine purchase currency by book type: limited -> Stars, regular -> coins
  const isStarsPurchase = !!book.isLimited;
  const isSoldOut = isStarsPurchase && (book.remainingCopies ?? 0) === 0;
  const displayPrice = isStarsPurchase ? book.priceInStars : book.price ?? 0;

  // Add affordability check (for coin purchases only, Stars purchases are always available)
  const canAfford =
    (isStarsPurchase || userBalance >= (book.price ?? 0)) && !isSoldOut;

  const handlePurchaseClick = useCallback(() => {
    if (!book.isOwned && !isPurchasing && canAfford) {
      if (
        isFirstNewBook &&
        !StorageService.isOneTimeHintDismissed(
          StorageService.ONE_TIME_HINT_KEYS.FIRST_BOOK_BUY_BUTTON
        )
      ) {
        StorageService.dismissOneTimeHint(
          StorageService.ONE_TIME_HINT_KEYS.FIRST_BOOK_BUY_BUTTON
        );
      }
      onPurchaseBook(book.id);
    }
  }, [
    book.id,
    book.isOwned,
    onPurchaseBook,
    isPurchasing,
    canAfford,
    isFirstNewBook,
  ]);

  const bookTitle = book.nameKey ? t(book.nameKey) : book.id;

  return (
    <div
      className={`book-card book-card--${book.rarity.toLowerCase()} ${
        isSoldOut ? "book-card--sold-out" : ""
      }`}
    >
      {/* Book Title */}
      <h3 className="book-card__title">{bookTitle}</h3>

      {/* Image and Details Container */}
      <div className="book-card__content">
        <div className="book-card__image-container">
          {typeof book.bookNumber === "number" && (
            <div className="book-card__number-badge">#{book.bookNumber}</div>
          )}
          <img
            src={`/assets/img/books/${book.imageKey}.avif`}
            alt={bookTitle}
            className="book-card__image"
          />
        </div>

        <div className="book-card__details">
          {/* Rarity */}
          <div className="book-card__details-content">
            <div
              className={`book-card__rarity book-card__rarity--${book.rarity.toLowerCase()}`}
            >
              {t(`looks.rarity.${book.rarity.toLowerCase()}`, {
                defaultValue: book.rarity,
              })}
            </div>

            {/* Mana Generation */}
            <div className="book-card__mana">
              +{book.manaGenerationIncrement} {t("magicBooks.manaPerDay")}
            </div>

            {/* Limited Book Counter */}
            {book.isLimited &&
              book.remainingCopies !== undefined &&
              book.remainingCopies > 0 && (
                <div className="book-card__remaining">
                  <div className="book-card__remaining-line">
                    {t("toolkit.books.promo.onlyRemainingLeft", {
                      defaultValue: "Only {{count}} left",
                      count: book.remainingCopies,
                    })}
                  </div>
                  <div className="book-card__remaining-line">
                    {t("toolkit.books.promo.onlyRemainingCta", {
                      defaultValue: "Don't miss out",
                    })}
                  </div>
                </div>
              )}
            {book.isLimited &&
              book.remainingCopies !== undefined &&
              book.remainingCopies === 0 && (
                <div className="book-card__remaining">
                  {t("magicBooks.soldOut", { defaultValue: "Sold out" })}
                </div>
              )}
          </div>

          {/* Purchase Button or No Button for Owned */}
          {!book.isOwned && (
            <div className="book-card__purchase-button-wrapper">
              <Button
                size="s"
                mode="filled"
                className={`book-card__purchase-button ${
                  !canAfford ? "book-card__purchase-button--disabled" : ""
                }`}
                onClick={handlePurchaseClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                disabled={isPurchasing || !canAfford}
              >
                <span className="book-card__purchase-button-content">
                  {t("magicBooks.buy")} {displayPrice}{" "}
                  {isStarsPurchase ? (
                    <img
                      src="/assets/img/star.webp"
                      alt="star"
                      className="book-card__coin-icon"
                    />
                  ) : (
                    <img
                      src="/assets/img/ui/coin.webp"
                      alt="coin"
                      className="book-card__coin-icon"
                    />
                  )}
                </span>
              </Button>
              {isFirstNewBook &&
                !StorageService.isOneTimeHintDismissed(
                  StorageService.ONE_TIME_HINT_KEYS.FIRST_BOOK_BUY_BUTTON
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

interface MagicBooksProps {
  userBalance?: number; // Add optional userBalance prop
  onBalanceUpdate?: (newBalance: number) => void; // Add callback for balance updates
}

export function MagicBooks({
  userBalance = 0,
  onBalanceUpdate,
}: MagicBooksProps): JSX.Element {
  const { t } = useI18n();
  // Add prop with default
  const [books, setBooks] = useState<BookInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const apiService = ApiService.getInstance();

  // Memoize fetchBooks to prevent recreation on every render
  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch both overall books (coins) and purchases (limited, stars)
      const [booksData, purchasesData] = await Promise.all([
        apiService.api.booksList(),
        apiService.api.purchasesList(),
      ]);

      const allBooks = booksData?.books ?? [];
      const limitedFromPurchases = purchasesData?.books ?? [];

      // Build a map to merge, preferring purchases data for limited books
      const mergedById = new Map<string, BookInfo>();
      for (const book of allBooks) {
        mergedById.set(book.id, book);
      }
      for (const limited of limitedFromPurchases) {
        mergedById.set(limited.id, limited);
      }

      setBooks(Array.from(mergedById.values()));
    } catch (err) {
      console.error("Failed to fetch books:", err);
      setError(t("magicBooks.errorLoadingBooks"));
    } finally {
      setLoading(false);
    }
  }, [apiService, t]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  // Optimized purchase handler that updates only the specific book
  const handlePurchaseBook = useCallback(
    async (bookId: string) => {
      try {
        setPurchasing(bookId);
        setError(null);

        // Find the book to get its price
        const book = books.find((b) => b.id === bookId);
        if (!book) {
          setError(t("magicBooks.errorPurchaseBook"));
          return;
        }

        if (book.isLimited) {
          // Limited books are purchased with Stars via unified purchases API
          const response = await apiService.api.purchasesBuyCreate({
            bookId: book.id,
          });

          const { invoiceUrl } = response;

          if (invoiceUrl && openInvoice.isAvailable()) {
            const status = await openInvoice(invoiceUrl, "url");
            if (status !== "paid") {
              setError(t("toolkit.shop.errorPayment"));
            } else {
              // Wait for 1 second before updating
              await new Promise((resolve) => setTimeout(resolve, 1000));

              // Update the specific book's ownership status
              setBooks((prevBooks) =>
                prevBooks.map((b) =>
                  b.id === bookId ? { ...b, isOwned: true } : b
                )
              );
            }
          } else {
            setError(t("toolkit.shop.errorPayment"));
          }
        } else {
          // Regular books are purchased with coins via books API
          const response = await apiService.api.booksBuyCreate(book.id);

          if (response?.success) {
            // Update ownership
            setBooks((prevBooks) =>
              prevBooks.map((b) =>
                b.id === bookId ? { ...b, isOwned: true } : b
              )
            );

            // Subtract coin price from balance if available
            const price = book.price ?? 0;
            onBalanceUpdate?.(Math.max(0, userBalance - price));
          } else {
            setError(t("magicBooks.errorPurchaseBook"));
          }
        }
      } catch (err) {
        console.error("Failed to purchase book:", err);
        setError(t("magicBooks.errorPurchaseBook"));
      } finally {
        setPurchasing(null);
      }
    },
    [apiService, books, userBalance, onBalanceUpdate, t]
  );

  const handleNavigateToShop = useCallback(() => {
    navigate("/shop");
  }, [navigate]);

  // Compute sections: owned, limited available, regular available, sold out
  const {
    ownedBooks,
    limitedAvailableBooks,
    regularAvailableBooks,
    soldOutBooks,
  } = useMemo(() => {
    const owned = books.filter((b) => b.isOwned);
    const limitedAvail = books.filter(
      (b) => b.isLimited && !b.isOwned && (b.remainingCopies ?? 0) > 0
    );
    const regularAvail = books.filter((b) => !b.isLimited && !b.isOwned);
    const soldOut = books.filter(
      (b) => b.isLimited && !b.isOwned && (b.remainingCopies ?? 0) === 0
    );
    return {
      ownedBooks: owned,
      limitedAvailableBooks: limitedAvail,
      regularAvailableBooks: regularAvail,
      soldOutBooks: soldOut,
    };
  }, [books]);

  const renderSectionHeader = useCallback(
    (title: string) => (
      <div className="magic-books__section-header">
        <h2 className="magic-books__section-title">{title}</h2>
      </div>
    ),
    []
  );

  const renderBookCards = useCallback(
    (sectionBooks: BookInfo[], markFirstNew: boolean = false) =>
      sectionBooks.map((book, idx) => (
        <BookCard
          key={book.id}
          book={book}
          onPurchaseBook={handlePurchaseBook}
          isPurchasing={purchasing === book.id}
          userBalance={userBalance}
          isFirstNewBook={markFirstNew && idx === 0}
        />
      )),
    [handlePurchaseBook, purchasing, userBalance]
  );

  return (
    <div className="magic-books" ref={scrollContainerRef}>
      {/* Header */}
      <div className="magic-books__header">
        <h1 className="magic-books__title">{t("magicBooks.title")}</h1>
        <p className="magic-books__subtitle">{t("magicBooks.subtitle")}</p>
        {error && <div className="magic-books__error">{error}</div>}
      </div>

      {/* Cards Container */}
      <div className="magic-books__cards-container">
        {/* Coins Widget */}
        <div className="magic-books__coins-widget">
          <img
            src="/assets/img/ui/coin.webp"
            alt="Coins"
            className="magic-books__coins-icon"
          />
          <div className="magic-books__coins-value-container">
            <div className="magic-books__coins-value">{userBalance}</div>
            <div className="magic-books__coins-label">{t("common.coins")}</div>
          </div>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="magic-books__add-coins-icon"
            onClick={handleNavigateToShop}
          >
            <path
              d="M12 8V16M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="magic-books__cards">
          {/* Loading State */}
          {loading && (
            <div className="magic-books__loading">{t("common.loading")}...</div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="magic-books__error">{error}</div>
          )}

          {/* No Books State */}
          {!loading && !error && books.length === 0 && (
            <div className="magic-books__no-books">
              {t("magicBooks.noBooksAvailable")}
            </div>
          )}

          {/* Books with Sections */}
          {!loading && !error && books.length > 0 && (
            <>
              {/* Owned Books Section */}
              {ownedBooks.length > 0 && (
                <>
                  {renderSectionHeader(t("magicBooks.sections.owned"))}
                  {renderBookCards(ownedBooks)}
                </>
              )}

              {/* Limited Availability Section */}
              {limitedAvailableBooks.length > 0 && (
                <>
                  {renderSectionHeader(
                    t("magicBooks.sections.limitedAvailability", {
                      defaultValue: "Limited availability",
                    })
                  )}
                  {renderBookCards(limitedAvailableBooks)}
                </>
              )}

              {/* New Books Section */}
              {regularAvailableBooks.length > 0 && (
                <>
                  {renderSectionHeader(t("magicBooks.sections.new"))}
                  {renderBookCards(regularAvailableBooks, true)}
                </>
              )}

              {/* Sold Out Section */}
              {soldOutBooks.length > 0 && (
                <>
                  {renderSectionHeader(
                    t("magicBooks.sections.soldOut", {
                      defaultValue: "Sold out",
                    })
                  )}
                  {renderBookCards(soldOutBooks)}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
